import * as React from "react";
import { IconPlayerPause, IconPlayerPlay } from "@tabler/icons-react";
import { cloneState, course, HEIGHT, MAX_TICKS, step, WIDTH } from "./engine.js";
import { createFlightRenderer, loadFlightArt } from "./flight-art.js";
import { flightRecords, nextFlightRecord } from "./flight-records.js";
import { FLIGHT_STEP_MS, interpolateFlightState } from "./flight-motion.js";

const RESOLUTION_SAMPLE_FRAMES = 90;
const SLOW_FRAME_MS = 22;
const RESOLUTION_STEP = .5;

/** @param {{ session: object, paused: boolean, recovering?: boolean, saving?: boolean, daily: number, dailyLimit?: number, leaders?: object[], practice: boolean, onProgress: Function, onCheckpoint: Function, onFinish: Function, onPause: Function, onResume: Function, onExit: Function }} props */
export function FlightScene({ session, paused, recovering = false, saving = false, daily, dailyLimit = 20, leaders = [], practice, onProgress, onCheckpoint, onFinish, onPause, onResume, onExit }) {
  const canvas = React.useRef(null);
  const background = React.useRef(null);
  const art = React.useRef(null);
  const [artReady, setArtReady] = React.useState(false);
  const [artError, setArtError] = React.useState(false);
  const engine = React.useRef(cloneState(session.state));
  const inputs = React.useRef([]);
  const queue = React.useRef(false);
  const callbacks = React.useRef({ onProgress, onCheckpoint, onFinish, onPause, onResume });
  callbacks.current = { onProgress, onCheckpoint, onFinish, onPause, onResume };
  const blocked = paused || recovering || saving;
  const pausedRef = React.useRef(blocked); pausedRef.current = blocked;
  const userPaused = React.useRef(paused); userPaused.current = paused;
  const wasBlocked = React.useRef(blocked);
  const dailyRef = React.useRef(daily); dailyRef.current = daily;
  const dailyLimitRef = React.useRef(dailyLimit); dailyLimitRef.current = dailyLimit;
  const [score, setScore] = React.useState(session.state.score);
  const records = React.useMemo(() => flightRecords(leaders), [leaders]);
  const recordsRef = React.useRef(records); recordsRef.current = records;
  const nextRecord = nextFlightRecord(records, score);
  const [countdown, setCountdown] = React.useState(3);
  const ready = React.useRef(false);
  const started = React.useRef(session.state.tick > 0);
  const [startedUi, setStartedUi] = React.useState(session.state.tick > 0);
  const lastCheckpoint = React.useRef(session.state.tick);
  const finished = React.useRef(false);

  React.useLayoutEffect(() => {
    if (blocked) queue.current = false;
    if (wasBlocked.current && !blocked && !finished.current) { ready.current = false; setCountdown(3); }
    wasBlocked.current = blocked;
  }, [blocked]);

  React.useEffect(() => {
    if (!artReady) return;
    if (blocked) { ready.current = false; return; }
    if (!countdown) { ready.current = true; return; }
    ready.current = false;
    const timer = setTimeout(() => setCountdown(value => value - 1), 650);
    return () => clearTimeout(timer);
  }, [countdown, blocked, artReady]);

  const packet = () => ({ state: cloneState(engine.current), inputs: [...inputs.current] });
  function pauseNow() {
    if (pausedRef.current || finished.current) return;
    pausedRef.current = true; queue.current = false;
    callbacks.current.onPause(packet());
  }
  function flap() {
    if (!art.current || !ready.current || pausedRef.current || finished.current) return;
    if (engine.current.tick + 1 - engine.current.last_flap < 6) return;
    started.current = true; setStartedUi(true); queue.current = true;
  }

  React.useEffect(() => {
    const key = event => {
      if (event.code === "Escape") pauseNow();
      if (event.code === "Space" && !event.repeat && !["BUTTON", "INPUT", "SELECT"].includes(event.target.tagName)) {
        event.preventDefault(); flap();
      }
    };
    const visibility = () => {
      // Hiding during reconnection is still an intentional pause; do not auto-resume in the background.
      if (document.hidden && !userPaused.current && !finished.current) {
        pausedRef.current = true; queue.current = false;
        callbacks.current.onPause(packet());
      }
    };
    window.addEventListener("keydown", key); document.addEventListener("visibilitychange", visibility);
    return () => { window.removeEventListener("keydown", key); document.removeEventListener("visibilitychange", visibility); };
  }, []);

  React.useEffect(() => {
    const element = canvas.current, ctx = element.getContext("2d");
    const backdrop = background.current, backgroundContext = backdrop.getContext("2d", { alpha: false });
    const gates = course(session.week.seed, session.week.version);
    let disposed = false;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let frame, previous = 0, accumulated = 0, renderScene;
    let ratio = Math.min(2, window.devicePixelRatio || 1);
    let sampleTime = 0, samples = 0, drawnTick = -1;
    const previousState = { ...engine.current }, visualState = { ...engine.current };
    let activeBefore = false;
    const resize = () => {
      element.width = WIDTH * ratio; element.height = HEIGHT * ratio; ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      backdrop.width = element.width; backdrop.height = element.height;
      backgroundContext.setTransform(ratio, 0, 0, ratio, 0, 0);
      if (art.current) renderScene = createFlightRenderer(ctx, art.current, backgroundContext);
      drawnTick = -1;
    };
    resize(); window.addEventListener("resize", resize);
    const loop = time => {
      const dt = previous ? time - previous : 0; previous = time;
      const active = art.current && !pausedRef.current && ready.current && started.current && !finished.current;
      if (active) {
        if (!activeBefore) { accumulated = 0; Object.assign(previousState, engine.current); }
        // Sustained slow frames lower only raster resolution; integer physics stays at 60 Hz.
        if (dt > 0 && dt <= 500) {
          sampleTime += dt; samples++;
          if (samples >= RESOLUTION_SAMPLE_FRAMES) {
            if (sampleTime / samples > SLOW_FRAME_MS && ratio > 1) { ratio = Math.max(1, ratio - RESOLUTION_STEP); resize(); }
            sampleTime = 0; samples = 0;
          }
        }
        if (dt > 500) { accumulated = 0; pauseNow(); }
        else if (activeBefore) accumulated += dt;
        const previousScore = engine.current.score;
        while (accumulated >= FLIGHT_STEP_MS && !pausedRef.current && !finished.current) {
          Object.assign(previousState, engine.current);
          if (queue.current) inputs.current.push(engine.current.tick + 1);
          step(engine.current, queue.current, gates, session.week.version); queue.current = false; accumulated -= FLIGHT_STEP_MS;
          callbacks.current.onProgress({ state: engine.current, inputs: inputs.current });
          if (!engine.current.alive || engine.current.tick >= MAX_TICKS) {
            finished.current = true; callbacks.current.onFinish(packet());
          } else if (engine.current.tick - lastCheckpoint.current >= 300) {
            lastCheckpoint.current = engine.current.tick; callbacks.current.onCheckpoint(packet());
          }
        }
        if (engine.current.score !== previousScore) setScore(engine.current.score);
      } else { accumulated = 0; sampleTime = 0; samples = 0; }
      const moving = active && !pausedRef.current && !finished.current;
      const state = moving ? interpolateFlightState(visualState, previousState, engine.current, accumulated / FLIGHT_STEP_MS) : engine.current;
      // Interpolate at display refresh rate; stopped scenes still paint only once.
      if (renderScene && (moving || drawnTick !== state.tick)) {
        renderScene(state, gates, dailyRef.current, dailyLimitRef.current, practice, reducedMotion, recordsRef.current);
        drawnTick = state.tick;
      }
      activeBefore = moving;
      frame = requestAnimationFrame(loop);
    };
    loadFlightArt().then(images => {
      if (disposed) return;
      art.current = images; resize(); setArtReady(true); frame = requestAnimationFrame(loop);
    }).catch(() => { if (!disposed) setArtError(true); });
    return () => { disposed = true; cancelAnimationFrame(frame); window.removeEventListener("resize", resize); };
  }, [session.id]);

  return <div className="nf-play">
    <div className="nf-hud"><b aria-label={`Очки: ${score}`}>{score}</b>
      <button type="button" onClick={pauseNow} disabled={blocked} aria-label="Пауза"><IconPlayerPause /></button></div>
    {!!records.length && <aside className="nf-record-target" aria-label="Рекорды топ-10 на старте полёта">
      {nextRecord ? <><small>{score === nextRecord.score ? "Рекорд повторён" : "Следующий рекорд"}</small>
        <b>{nextRecord.label}</b><span>{nextRecord.score} очков</span></> : <b>Все рекорды топ-10 пройдены</b>}
    </aside>}
    <div className="nf-field" role="button" tabIndex={0} aria-label="Нажимайте или используйте пробел, чтобы подняться" onPointerDown={event => { event.preventDefault(); flap(); }}>
      <canvas className="nf-foreground" ref={canvas} width={WIDTH} height={HEIGHT} aria-label={`Полёт: ${score} очков`} />
      <canvas className="nf-background" ref={background} width={WIDTH} height={HEIGHT} aria-hidden="true" />
      {!artReady && <div className="nf-art-loading" role="status" onPointerDown={e => e.stopPropagation()}>{artError ? <><span>Не удалось загрузить сцену</span><button type="button" onClick={onExit}>В лобби</button></> : "Готовим ночной полёт…"}</div>}
      {artReady && !blocked && (countdown > 0 || !startedUi) && <div className="nf-ready">{countdown || "Нажми для полёта"}</div>}
      {(recovering || saving) && <div className="nf-pause" role="status" onPointerDown={e => e.stopPropagation()}><b>{recovering ? "Восстанавливаем связь…" : "Сохраняем результат…"}</b><span>Позиция сохранена. Повторяем автоматически.</span></div>}
      {paused && !recovering && !saving && <div className="nf-pause" onPointerDown={e => e.stopPropagation()}><IconPlayerPause /><b>Полёт на паузе</b><button type="button" onClick={onResume}><IconPlayerPlay /> Продолжить</button><button type="button" className="nf-pause-exit" onClick={onExit}>В лобби</button></div>}
    </div>
    <p className="nf-instruction">Нажми — взлети · Не касайся препятствий</p>
    <p className="nf-daily">{practice ? "Без рейтинга и начисления валюты" : daily >= dailyLimit ? `Лимит ${dailyLimit}/${dailyLimit} собран · Возвращайся завтра` : `Подтверждено сегодня: ${daily}/${dailyLimit}`}</p>
  </div>;
}
